"use client";

import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { routes, protectedRoutes } from "@/resources";
import NotFound from "@/app/not-found";

interface RouteGuardProps {
  children: React.ReactNode;
}

/* Route checks are pure config lookups, so they run synchronously during
   SSR and the server HTML already carries the real page. Anything async
   (the password gate) stays scoped to protected routes only. The gate UI
   uses plain elements on the oi-* utility classes — pulling Once UI
   components in here would put its whole barrel in every page's client
   graph for a screen no current route renders. */
const checkRouteEnabled = (pathname: string): boolean => {
  if (!pathname) return false;

  if (pathname in routes) {
    return routes[pathname as keyof typeof routes];
  }

  // /university/[slug] and /lab/[slug] detail pages are always accessible
  // even though their listings are merged into /work
  if (pathname.startsWith("/university/")) return true;
  if (pathname === "/lab" || pathname.startsWith("/lab/")) return true;

  const dynamicRoutes = ["/blog", "/work"] as const;
  for (const route of dynamicRoutes) {
    if (pathname.startsWith(route) && routes[route]) {
      return true;
    }
  }

  return false;
};

const gateStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "var(--oi-s4)",
  width: "100%",
  maxWidth: "24rem",
  margin: "0 auto",
  padding: "var(--oi-s8) var(--oi-gap)",
};

const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const pathname = usePathname() ?? "";
  const isRouteEnabled = checkRouteEnabled(pathname);
  const isPasswordRequired = Boolean(
    protectedRoutes[pathname as keyof typeof protectedRoutes],
  );

  const [password, setPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(isPasswordRequired);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!isPasswordRequired) return;
    let cancelled = false;
    setCheckingAuth(true);
    setIsAuthenticated(false);
    fetch("/api/check-auth")
      .then((response) => {
        if (!cancelled && response.ok) setIsAuthenticated(true);
      })
      .finally(() => {
        if (!cancelled) setCheckingAuth(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pathname, isPasswordRequired]);

  const handlePasswordSubmit = async () => {
    const response = await fetch("/api/authenticate", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });

    if (response.ok) {
      setIsAuthenticated(true);
      setError(undefined);
    } else {
      setError("Incorrect password");
    }
  };

  if (!isRouteEnabled) {
    return <NotFound />;
  }

  if (isPasswordRequired && checkingAuth) {
    return (
      <div style={gateStyle} aria-busy="true">
        <span className="oi-sm oi-muted">Checking access…</span>
      </div>
    );
  }

  if (isPasswordRequired && !isAuthenticated) {
    return (
      <div style={gateStyle}>
        <h1 className="oi-h3" style={{ textAlign: "center" }}>
          This page is password protected
        </h1>
        <form
          style={{ display: "contents" }}
          onSubmit={(e) => {
            e.preventDefault();
            handlePasswordSubmit();
          }}
        >
          <label className="oi-xs oi-faint" htmlFor="password">
            Password
          </label>
          <input
            id="password"
            type="password"
            className="oi-sm"
            style={{
              width: "100%",
              padding: "var(--oi-s2) var(--oi-s3)",
              background: "var(--oi-card)",
              color: "var(--oi-ink)",
              border: "1px solid var(--oi-hairline)",
            }}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          {error && (
            <span role="alert" className="oi-xs" style={{ color: "#c25a5a" }}>
              {error}
            </span>
          )}
          <button type="submit" className="oi-btn is-white">
            Submit
          </button>
        </form>
      </div>
    );
  }

  return <>{children}</>;
};

export { RouteGuard };
